import { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd'
import {
  EditOutlined,
  PlusOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useAuth } from '@/contexts/AuthContext'
import {
  Workshop,
  WorkshopCreate,
  useCreateWorkshop,
  useDeleteWorkshop,
  useUpdateWorkshop,
  useWorkshops,
} from '@/hooks/useWorkshops'
import { useWorkshopTypes } from '@/hooks/useSystemTables'
import axiosInstance from '@/lib/axios'
import { registraPermesso } from '@/lib/raccoltaPermessi'

const { Text } = Typography

export function OfficinePage() {
  const { setTitle } = usePageTitle()
  useEffect(() => { setTitle('Gestione Officine') }, [setTitle])

  const { permissions } = useAuth()
  const [showInactive, setShowInactive] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Workshop | null>(null)
  const [form] = Form.useForm<WorkshopCreate>()
  const [userOptions, setUserOptions] = useState<{ label: string; value: number }[]>([])

  const { data, isLoading } = useWorkshops(showInactive ? undefined : true)
  const { data: workshopTypes = [] } = useWorkshopTypes()
  const createMutation = useCreateWorkshop()
  const updateMutation = useUpdateWorkshop()
  const deleteMutation = useDeleteWorkshop()

  const getTipoTag = (tipo: string) => {
    const wt = workshopTypes.find(t => t.codice === tipo)
    return <Tag color={wt?.colore ?? 'default'}>{wt?.nome ?? tipo}</Tag>
  }

  const workshops = data?.items ?? []

  // Carica utenti per il selettore responsabile (solo chi ha il permesso system.manage_users)
  useEffect(() => {
    registraPermesso('system.manage_users')
    if (!permissions.includes('system.manage_users')) return
    axiosInstance.get('/users/')
      .then((res) => {
        const users: any[] = res.data.items || res.data || []
        setUserOptions(
          users
            .filter((u) => u.attivo)
            .map((u) => ({ label: `${u.nome} ${u.cognome} (${u.ruolo ?? u.ruolo_id})`, value: u.id }))
        )
      })
      .catch(() => {/* silently ignore */})
  }, [permissions])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('attivo', true)
    setModalOpen(true)
  }

  const openEdit = (w: Workshop) => {
    setEditing(w)
    form.setFieldsValue({
      nome: w.nome,
      tipo: w.tipo,
      indirizzo: w.indirizzo ?? '',
      email: w.email ?? '',
      telefono1: w.telefono1 ?? '',
      telefono2: w.telefono2 ?? '',
      telefono3: w.telefono3 ?? '',
      responsabile_id: w.responsabile_id ?? undefined,
      esterna: w.esterna ?? false,
      attivo: w.attivo,
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, data: values })
    } else {
      await createMutation.mutateAsync(values)
    }
    setModalOpen(false)
  }

  const columns: ColumnsType<Workshop> = [
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      render: (nome: string, record) => (
        <Space>
          <Text strong={record.attivo}>{nome}</Text>
          {!record.attivo && <Tag color="default">Disattiva</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 130,
      render: (tipo: string) => getTipoTag(tipo),
    },
    {
      title: 'Responsabile',
      dataIndex: 'responsabile_id',
      key: 'responsabile_id',
      width: 180,
      render: (rid: number | null) => {
        const found = userOptions.find((u) => u.value === rid)
        return found ? <Text>{found.label}</Text> : <Text type="secondary">—</Text>
      },
    },
    {
      title: 'Indirizzo',
      dataIndex: 'indirizzo',
      key: 'indirizzo',
      render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Utenti',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 90,
      render: (n: number) => (
        <Space>
          <TeamOutlined />
          {n}
        </Space>
      ),
    },
    {
      title: 'Tipo officina',
      dataIndex: 'esterna',
      key: 'esterna',
      width: 120,
      render: (esterna: boolean) =>
        esterna
          ? <Tag color="orange">Esterna</Tag>
          : <Tag color="blue">Interna</Tag>,
    },
    {
      title: 'Stato',
      dataIndex: 'attivo',
      key: 'attivo',
      width: 90,
      render: (attivo: boolean) =>
        attivo ? <Tag color="green">Attiva</Tag> : <Tag color="default">Inattiva</Tag>,
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          {record.attivo && (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => deleteMutation.mutate(record.id)}
              title="Disattiva officina"
            />
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '0 0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Text type="secondary">Mostra inattive</Text>
          <Switch checked={showInactive} onChange={setShowInactive} size="small" />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nuova Officina
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={workshops}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: 'Nessuna officina registrata' }}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Modifica: ${editing.nome}` : 'Nuova Officina'}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="nome" label="Nome officina" rules={[{ required: true }]}>
            <Input placeholder="Es. Officina Meccanica Roma" />
          </Form.Item>
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
            <Select placeholder="Seleziona tipo">
              {workshopTypes.filter(t => t.attivo).map(t => (
                <Select.Option key={t.codice} value={t.codice}>
                  <Tag color={t.colore} style={{ marginRight: 6 }}>{t.nome}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="responsabile_id" label="Responsabile">
            <Select
              placeholder="Seleziona responsabile (opzionale)"
              options={userOptions}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="indirizzo" label="Indirizzo">
            <Input placeholder="Via, città..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Formato email non valido' }]}
              >
                <Input placeholder="info@officina.it" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="telefono1" label="Telefono 1">
                <Input placeholder="+39 02 1234567" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="telefono2" label="Telefono 2">
                <Input placeholder="Opzionale" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="telefono3" label="Telefono 3">
                <Input placeholder="Opzionale" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="esterna" label="Officina esterna (subappalto)" valuePropName="checked">
            <Switch />
          </Form.Item>
          {editing && (
            <Form.Item name="attivo" label="Attiva" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}

export default OfficinePage
